import {
    Box,
    Button,
    Dialog,
    DialogContent,
    DialogTitle,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import { enqueueSnackbar } from "notistack";
import { useEffect, useState } from "react";
import CustomTooltip from "src/components/competitive-intelligence/components/CustomTooltip";
import Iconify from "src/components/iconify";
import { FlagIcon } from "src/components/iconify/flagIcon";
import CustomLineChart from "src/components/inline-chart/inline-chart-activations-scatter";
import { useUpdateKoBiddingAutoMaxCpcMutation } from "src/context/api/activations/api";
import { useCurrentWorkspace } from "src/context/reducers/app-settings";
import countriesJson from "src/utils/countries.json";

export default function ModalActivations({
    open,
    row,
    onClose,
    setLocalPatches,
}: {
    open: boolean;
    row: any;
    onClose: () => void;
    setLocalPatches: any;
}) {
    const workspace = useCurrentWorkspace();

    const rowActivation = row?.activations_details?.find?.((d: any) => d.mode.startsWith("KO Bidding Auto")) || null;

    const [maxCpc, setMaxCpc] = useState<number | "">("");
    const [threshold, setThreshold] = useState<number | "">("");

    const [updateKoBiddingAuto] = useUpdateKoBiddingAutoMaxCpcMutation();

    useEffect(() => {
        if (rowActivation?.max_cpc !== undefined) {
            setMaxCpc(rowActivation.max_cpc);
        }
        if (rowActivation?.accepted_variation_threshold !== undefined) {
            setThreshold(rowActivation.accepted_variation_threshold);
        }
    }, [rowActivation]);

    const handleApplyChanges = async () => {
        try {

            await updateKoBiddingAuto({
                id: rowActivation?.id,
                workspaceId: workspace?.id,
                accepted_variation_threshold: Number(threshold),
                mode: "KO Bidding Auto - " + selectedKpi
            }).unwrap();

            setLocalPatches((prev: any) => {
                const existing = prev[row.id] ?? row;

                return {
                    ...prev,
                    [row.id]: {
                        ...existing,
                        activations_details: existing.activations_details.map((d: any) =>
                            d.id === rowActivation.id
                                ? {
                                    ...d,
                                    accepted_variation_threshold: Number(threshold),
                                    mode: "KO Bidding Auto - " + selectedKpi
                                }
                                : d
                        )
                    }
                };
            });

            enqueueSnackbar("Changes applied successfully", { variant: "success" });
            onClose();
        } catch (err) {
            enqueueSnackbar("Failed to update KO Bidding Auto", { variant: "error" });
        }
    };

    const [selectedKpi, setSelectedKpi] = useState<string | null>(null);

    useEffect(() => {
        if (rowActivation?.mode) {
            setSelectedKpi(rowActivation.mode.split("-")[1]?.trimStart())
        }
    }, [rowActivation]);

    return (
        <Dialog
            open={open && !!row}
            onClose={onClose}
            maxWidth="xl"
            fullWidth
            PaperProps={{
                sx: {
                    color: "#FFF",
                },
            }}
        >
            <DialogTitle sx={{
                padding: "24px",
                backgroundColor: 'rgba(226, 229, 233, 0.16)'
            }}>
                <Box display="flex" justifyContent="space-between">
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "row",
                            justifyContent: "space-between",
                            alignItems: "center",
                            width: "100%",
                        }}
                    >
                        <Typography color={"rgba(51, 60, 71, 1)"} fontSize={16}>
                            {row?.keyword}
                        </Typography>

                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: "row",
                                gap: 0.8,
                                alignItems: "center",
                            }}
                        >
                            <Box sx={{ position: "relative", top: -3 }}>
                                <FlagIcon
                                    isCircle={false}
                                    code={countriesJson
                                        .find((c) => c.name === row?.region_name)
                                        ?.code?.toLowerCase()}
                                    sx={{ width: 23, height: 16, position: 'relative', top:2 }}
                                />
                            </Box>

                            <Typography
                                color={"rgba(51, 60, 71, 1)"}
                                sx={{
                                    whiteSpace: "nowrap",
                                    fontSize: 14,
                                }}
                            >
                                {row?.campaign_names}
                            </Typography>

                            <Box
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onClose();
                                }}
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    width: 22,
                                    height: 22,
                                    borderRadius: "6px",
                                    cursor: "pointer",
                                    color: "rgba(255,255,255,0.6)",
                                    transition: "all 0.15s ease",
                                    "&:hover": {
                                        backgroundColor: "rgba(255,255,255,0.12)",
                                        color: "#FFF",
                                    },
                                }}
                            >
                                <Iconify color={"rgba(94, 109, 123, 1)"} icon="material-symbols:close-rounded" width={16} />
                            </Box>
                        </Box>
                    </Box>
                </Box>
            </DialogTitle>

            <DialogContent
                sx={{
                    overflow: "visible",
                    position: "relative",
                    p: 0,
                    backgroundColor: 'white'
                }}
            >
                <Box
                    display="flex"
                    justifyContent="space-between"
                    gap={"18px"}
                    px={'24px'}
                    mb={"20px"}
                    mt={"32px"}
                >
                    {[
                        {
                            title: "Smart Top",
                            data: row?.scatter_plot_top_blended_presence_rate,
                            yField: "top_blended_presence_rate",
                            color: "#42A5F5",
                            span: "MOST SAVINGS",
                            bgColorSpan: "rgba(234, 246, 251, 1)",
                            colorSpan: "#338AF3",
                            colorBorder: "rgba(226, 229, 233, 0.16)",
                            definition: "How often your domain appears in the top section, whether paid or organic.",
                            avg_d1: row?.top_blended_presence_rate_d1
                        },
                        {
                            title: "Smart First",
                            data: row?.scatter_plot_abs_top_blended_presence_rate,
                            yField: "abs_top_blended_presence_rate",
                            color: "#66BB6A",
                            definition: "How often your domain appears in the first position in the top section, whether paid or organic.",
                            avg_d1: row?.abs_top_blended_presence_rate_d1
                        },
                        {
                            title: "Paid Top",
                            data: row?.scatter_plot_top_paid_presence_rate,
                            yField: "top_paid_presence_rate",
                            color: "#FFA726",
                            definition: "How often your domain appears in the paid top ads section.",
                            avg_d1: row?.top_paid_presence_rate_d1
                        },
                        {
                            title: "Paid First",
                            data: row?.scatter_plot_abs_top_paid_presence_rate,
                            yField: "abs_top_paid_presence_rate",
                            color: "#AB47BC",
                            span: "MOST VISIBILITY",
                            bgColorSpan: "rgba(247, 240, 255, 1)",
                            colorBorder: "rgba(226, 229, 233, 0.16)",
                            colorSpan: "rgba(118, 53, 220, 1)",
                            definition: "How often your domain appears in the first position in the paid top ads section.",
                            avg_d1: row?.abs_top_paid_presence_rate_d1
                        },

                    ]
                        .map((chart, i) => {
                            const isSelected = selectedKpi === chart.title;

                            const xValues = (chart.data || [])
                                .map((d: any) => Number(d.max_cpc))
                                .filter((v: number) => !isNaN(v));

                            const minX = xValues.length > 0 ? Math.min(...xValues) * 1 : undefined;

                            const maxX = xValues.length > 0 ? Math.max(...xValues) * 1 : undefined;

                            return (
                                <Box
                                    onClick={() => setSelectedKpi(chart.title)}
                                    key={i}
                                    sx={{
                                        flex: 1,
                                        cursor: "pointer",
                                        position: "relative",
                                        background: "#fdfdfd",
                                        border: isSelected
                                            ? "2px solid #469ce9"
                                            : "1px solid rgba(145, 158, 171, 0.12)",
                                        borderRadius: 3,
                                        p: 2.5,
                                        display: "flex",
                                        flexDirection: "column",
                                        justifyContent: "space-between",
                                        boxShadow: isSelected
                                            ? "0 0 12px rgba(66, 165, 245, 0.35)"
                                            : "none",
                                        transition: "all 0.2s ease",
                                    }}
                                >
                                    {isSelected && (
                                        <Box
                                            sx={{
                                                position: "absolute",
                                                top: -12,
                                                left: "10%",
                                                transform: "translateX(-10%)",
                                                bgcolor: "#E8F1FC",
                                                color: "#1E88E5",
                                                px: 1.2,
                                                py: 0.3,
                                                borderRadius: "6px",
                                                fontSize: 11,
                                                fontWeight: 700,
                                                textTransform: "uppercase",
                                                boxShadow: '0 0 6px 2px rgba(208, 232, 255, 0.10)',
                                                zIndex: 10,
                                            }}
                                        >
                                            SELECTED KPI
                                        </Box>
                                    )}

                                    <Box
                                        display="flex"
                                        pb={2}
                                    >
                                        <Box sx={{ width: "100%" }} display={"flex"} flexDirection={"row"} justifyContent={"space-between"}>
                                            <Box display={"flex"} flexDirection={"row"}>
                                                <Box display={"flex"} flexDirection={"row"} gap={1}>
                                                    <Typography color={"rgba(51, 60, 71, 1)"} fontWeight={600}>{chart.title}</Typography>

                                                    <CustomTooltip title={chart?.definition} placement="top">
                                                        <Box sx={{ position: "relative", top: 3 }}>
                                                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 16 16" fill="none">
                                                                <g clip-path="url(#clip0_1134_4695)">
                                                                    <path d="M6 8V6M6 4H6.005M11 6C11 8.76142 8.76142 11 6 11C3.23858 11 1 8.76142 1 6C1 3.23858 3.23858 1 6 1C8.76142 1 11 3.23858 11 6Z" stroke="#919EAB" stroke-opacity="0.6" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round" />
                                                                </g>
                                                                <defs>
                                                                    <clipPath id="clip0_1134_4695">
                                                                        <rect width="20" height="20" fill="white" />
                                                                    </clipPath>
                                                                </defs>
                                                            </svg>
                                                        </Box>
                                                    </CustomTooltip>
                                                </Box>

                                            </Box>

                                            {chart?.avg_d1
                                                ? <Box display={"flex"} flexDirection={"row"} gap={'8px'}>
                                                    <Iconify color="rgba(182, 182, 182, 1)" width={16} icon={"ph:function-bold"} />
                                                    <Typography color={"rgba(145, 158, 171, 1)"} fontSize={'14px'}>{chart?.avg_d1}%</Typography>
                                                </Box>
                                                : <></>
                                            }
                                        </Box>

                                        {chart.span && (
                                            <Box
                                                sx={{
                                                    bgcolor: chart?.bgColorSpan,
                                                    color: chart?.colorSpan,
                                                    px: 1.5,
                                                    py: 1,
                                                    borderRadius: 1,
                                                    display: "flex",
                                                    border: `1px solid ${chart?.colorBorder}`,
                                                    position: "absolute",
                                                    top: -15,
                                                    right: 20,
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                }}
                                            >
                                                <Typography
                                                    fontWeight={600}
                                                    fontSize={10}
                                                    sx={{ m: 0, lineHeight: 1 }}
                                                >
                                                    {chart.span}
                                                </Typography>
                                            </Box>
                                        )}
                                    </Box>

                                    <Box
                                        sx={{
                                            height: 250,
                                            background: "#fdfdfd",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                        }}
                                    >
                                        <CustomLineChart
                                            data={chart.data}
                                            yField={chart.yField}
                                            maxCpcValue={maxCpc}
                                            minX={minX}
                                            maxX={maxX}
                                            threshold={isSelected ? Number(threshold) : undefined}
                                        />
                                    </Box>
                                </Box>
                            );
                        })}

                </Box>

                <Box display={"flex"} justifyContent={"flex-end"} gap={2} px={"24px"} pb={'24px'}>
                    <Box display={"flex"} flexDirection={"row"}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 16 16" fill="none">
                            <g filter="url(#filter0_d_2803_11729)">
                                <circle cx="8" cy="8" r="3" fill="rgba(30, 136, 229, 1)" />
                            </g>
                            <defs>
                                <filter id="filter0_d_2803_11729" x="0" y="0" width="22" height="22" filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB">
                                    <feFlood flood-opacity="0" result="BackgroundImageFix" />
                                    <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha" />
                                    <feMorphology radius="1" operator="dilate" in="SourceAlpha" result="effect1_dropShadow_2803_11729" />
                                    <feOffset />
                                    <feGaussianBlur stdDeviation="2" />
                                    <feComposite in2="hardAlpha" operator="out" />
                                    <feColorMatrix type="matrix" values="0 0 0 0 0.921569 0 0 0 0 0.839216 0 0 0 0 0.992157 0 0 0 0.2 0" />
                                    <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_2803_11729" />
                                    <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_2803_11729" result="shape" />
                                </filter>
                            </defs>
                        </svg>
                        <Typography color={"rgba(30, 136, 229, 1)"} sx={{ opacity: .7 }} position={'relative'} top={2} fontSize={12}>Date & Day</Typography>
                    </Box>
                    <Box display={"flex"} flexDirection={"row"}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 16 16" fill="none">
                            <g filter="url(#filter0_d_2803_11729)">
                                <circle cx="8" cy="8" r="3" fill="#7635DC" />
                            </g>
                            <defs>
                                <filter id="filter0_d_2803_11729" x="0" y="0" width="22" height="22" filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB">
                                    <feFlood flood-opacity="0" result="BackgroundImageFix" />
                                    <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha" />
                                    <feMorphology radius="1" operator="dilate" in="SourceAlpha" result="effect1_dropShadow_2803_11729" />
                                    <feOffset />
                                    <feGaussianBlur stdDeviation="2" />
                                    <feComposite in2="hardAlpha" operator="out" />
                                    <feColorMatrix type="matrix" values="0 0 0 0 0.921569 0 0 0 0 0.839216 0 0 0 0 0.992157 0 0 0 0.2 0" />
                                    <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_2803_11729" />
                                    <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_2803_11729" result="shape" />
                                </filter>
                            </defs>
                        </svg>
                        <Typography color={"rgba(118, 53, 220, 1)"} sx={{ opacity: .8 }} position={'relative'} top={2} fontSize={12}>Previous day (D-1)</Typography>
                    </Box>
                </Box>

                <Box
                    sx={{
                        backgroundColor: "#faf6ff",
                        borderTop: "1px solid rgba(145, 158, 171, 0.20)",
                        width: "100%",
                        px: "20px",
                        display: "flex",
                        flexDirection: 'row',
                        justifyContent: 'space-between',
                        alignItems: "center",
                    }}
                >
                    <Box
                        sx={{
                            borderRadius: "6px",
                            background: "rgba(164, 111, 247, 0.2)",
                            display: "inline-flex",
                            alignItems: "center",
                            py: "2px",
                        }}
                    >
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                borderRadius: "4px",
                                p: .5,
                                background: "rgba(164, 111, 247, 0.2)"
                            }}
                        >
                            <Iconify color={"rgba(122, 37, 228, 1)"} icon="material-symbols:shield-outline-rounded" />
                        </Box>
                        <Typography
                            color="rgba(122, 37, 228, 1)"
                            sx={{
                                fontSize: 13,
                                fontWeight: 600,
                                px: '8px',
                                lineHeight: "22px",
                                m: 0,
                            }}
                        >
                            KO Bidding Auto
                        </Typography>
                    </Box>

                    <Box display={"flex"} flexDirection={"row"}>
                        <Box
                            sx={{
                                mr: '20px',
                                width: "1px",
                                height: '80px',
                                backgroundColor: "rgba(255, 255, 255, 0.10);",
                            }}
                        />
                        <Box display="flex" alignItems="center" gap={1.5}>

                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "stretch",
                                    width: "140px",
                                    height: "32px",
                                    borderRadius: "8px",
                                }}
                            >
                                <TextField
                                    label="Threshold"
                                    type="number"
                                    variant="outlined"
                                    size="small"
                                    value={threshold}
                                    onChange={(e) => {
                                        const val = e.target.value === "" ? "" : Number(e.target.value);
                                        if (val === "" || (val >= 1 && val <= 99)) setThreshold(val);
                                    }}
                                    sx={{
                                        width: 120,
                                        height: 20,
                                        "& .MuiInputBase-root.MuiOutlinedInput-root": {
                                            borderRadius: 0,
                                            borderTopLeftRadius: "8px",
                                            borderBottomLeftRadius: "8px",
                                            borderRight: "none",
                                        },

                                        "& .css-qdrsss-MuiInputBase-input-MuiOutlinedInput-input": {
                                            height: 15,
                                        },

                                        "& .MuiOutlinedInput-input": {
                                            textAlign: 'right'
                                        }
                                    }}
                                />

                                <Box
                                    sx={{
                                        display: "flex",
                                        width: "40px",
                                        justifyContent: "center",
                                        height: 37,
                                        alignItems: "center",
                                        backgroundColor: "#F2EBFE",
                                        borderLeft: "1px solid rgba(255,255,255,0.12)",
                                        borderTopRightRadius: "8px",
                                        borderBottomRightRadius: "8px",
                                    }}
                                >
                                    <Typography
                                        sx={{
                                            fontSize: 14,
                                            color: "rgba(177, 185, 196, 0.90)",
                                        }}
                                    >
                                        %
                                    </Typography>
                                </Box>
                            </Box>

                            <Button
                                variant="contained"
                                onClick={handleApplyChanges}
                                sx={{
                                    background: "white",
                                    color: "rgba(51, 60, 71, 1)",
                                    textTransform: "none",
                                    fontWeight: 600,
                                    fontSize: 13,
                                    px: 2,
                                    "&:hover": {
                                        background: "rgba(145, 158, 171, 0.50)",
                                    },
                                }}
                            >
                                Apply threshold
                            </Button>
                        </Box>
                    </Box>
                </Box>
            </DialogContent>
        </Dialog >
    );
}